import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Receipt, ArrowLeft, Crown, Loader2, Upload, Check } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { chunkedUpload } from "@/lib/upload";
import { startCheckout, PlanBadge } from "@/components/Upgrade";
import { TopBar } from "@/pages/Dashboard";

const money = (amt, cur) => `${(cur || "usd").toUpperCase() === "USD" ? "$" : ""}${Number(amt || 0).toFixed(2)}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); } catch { return ""; } };

export default function Billing() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [proBusy, setProBusy] = useState(false);
  const [brand, setBrand] = useState({ brand_name: "", brand_logo_file_id: null });
  const [brandBusy, setBrandBusy] = useState(false);

  const load = () => {
    api.get("/billing").then((r) => setData(r.data)).catch((e) => {
      if (e?.response?.status === 401) navigate("/login");
    }).finally(() => setLoading(false));
    api.get("/settings/branding").then((r) => setBrand({ brand_name: r.data.brand_name || "", brand_logo_file_id: r.data.brand_logo_file_id || null })).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startPro = async () => {
    setProBusy(true);
    try { await startCheckout("pro_monthly", null); }
    catch { toast.error("Could not start checkout"); setProBusy(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      toast.message("Uploading logo…");
      const up = await chunkedUpload(file, file.name, file.type, () => {});
      setBrand((b) => ({ ...b, brand_logo_file_id: up.file_id }));
      toast.success("Logo uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const saveBrand = async () => {
    setBrandBusy(true);
    try {
      await api.put("/settings/branding", { brand_name: brand.brand_name, brand_logo_file_id: brand.brand_logo_file_id });
      toast.success("Branding saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save branding"); }
    finally { setBrandBusy(false); }
  };

  const isPro = data?.plan === "pro";

  return (
    <div className="min-h-screen">
      <TopBar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <button data-testid="billing-back" onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors mb-4"><ArrowLeft size={16} /> Back to dashboard</button>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold">Billing & plans</h1>
        <p className="mt-1 text-[hsl(var(--muted-foreground))]">Your purchases and the plan for each tribute.</p>

        {loading ? (
          <div className="mt-8 h-40 rounded-2xl bg-[hsl(var(--muted))] animate-pulse" />
        ) : (
          <div className="mt-8 space-y-8">
            {/* Account plan */}
            <section className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 flex items-center justify-between gap-4">
              <div>
                <div className="text-sm text-[hsl(var(--muted-foreground))]">Account plan</div>
                <div className="mt-1 font-display text-xl font-semibold flex items-center gap-2">{isPro ? <><Crown size={18} className="text-[hsl(var(--accent))]" /> GatherVoices Pro</> : "Free"}</div>
              </div>
              {!isPro && (
                <button data-testid="upgrade-pro-button" onClick={startPro} disabled={proBusy}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 transition-opacity disabled:opacity-60">
                  {proBusy ? <Loader2 size={16} className="animate-spin" /> : <Crown size={16} />} Start Pro — $19/mo
                </button>
              )}
            </section>

            {/* Pro perks: branding */}
            {!isPro ? (
              <section className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6">
                <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Crown size={18} className="text-[hsl(var(--accent))]" /> Pro perks</h2>
                <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">Add your own logo & name to every tribute and watch page, export contributor lists, and run unlimited Keepsake-level events — ideal for funeral homes, planners, churches and schools.</p>
                <button data-testid="perks-upgrade-pro" onClick={startPro} disabled={proBusy} className="mt-4 inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-[hsl(var(--border))] font-semibold hover:bg-[hsl(var(--muted))]"><Crown size={16} /> Unlock Pro</button>
              </section>
            ) : (
              <section className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6" data-testid="branding-editor">
                <h2 className="font-display text-xl font-semibold">Custom branding</h2>
                <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">Shown on your tribute pages and family watch pages.</p>
                <div className="mt-4 flex flex-wrap items-end gap-4">
                  <div className="flex items-center gap-3">
                    <div className="h-16 w-16 rounded-xl border border-[hsl(var(--border))] overflow-hidden grid place-items-center bg-[hsl(var(--muted))]">
                      {brand.brand_logo_file_id ? <img src={fileUrl(brand.brand_logo_file_id)} alt="logo" className="h-full w-full object-cover" /> : <Upload size={20} className="text-[hsl(var(--muted-foreground))]" />}
                    </div>
                    <label className="text-sm font-semibold px-4 py-2.5 rounded-xl border border-[hsl(var(--border))] cursor-pointer hover:bg-[hsl(var(--muted))]">
                      Upload logo
                      <input data-testid="brand-logo-input" type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                    </label>
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <label className="text-sm font-medium">Display name</label>
                    <input data-testid="brand-name-input" value={brand.brand_name} onChange={(e) => setBrand((b) => ({ ...b, brand_name: e.target.value }))} placeholder="e.g. Willow & Sage Funeral Home"
                      className="mt-1 w-full rounded-xl border border-[hsl(var(--input))] bg-[hsl(var(--background))] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]" />
                  </div>
                  <button data-testid="brand-save-button" onClick={saveBrand} disabled={brandBusy} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-semibold hover:opacity-90 disabled:opacity-60">{brandBusy ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Save</button>
                </div>
              </section>
            )}

            {/* Per-event tiers */}
            <section>
              <h2 className="font-display text-xl font-semibold">Your tributes</h2>
              <div className="mt-3 divide-y divide-[hsl(var(--border))] rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                {(data?.events || []).length === 0 && <div className="p-6 text-sm text-[hsl(var(--muted-foreground))]">No tributes yet.</div>}
                {(data?.events || []).map((e) => (
                  <button key={e.event_id} data-testid={`billing-event-${e.event_id}`} onClick={() => navigate(`/events/${e.event_id}`)}
                    className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-[hsl(var(--muted))] transition-colors">
                    <span className="font-medium truncate">{e.title}</span>
                    <PlanBadge tier={e.tier} />
                  </button>
                ))}
              </div>
            </section>

            {/* Receipts */}
            <section>
              <h2 className="font-display text-xl font-semibold flex items-center gap-2"><Receipt size={18} /> Purchase history</h2>
              {(data?.transactions || []).length === 0 ? (
                <div className="mt-3 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-6 text-sm text-[hsl(var(--muted-foreground))]">No purchases yet. Upgrade a tribute to keep it forever.</div>
              ) : (
                <div className="mt-3 divide-y divide-[hsl(var(--border))] rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))]">
                  {data.transactions.map((t) => (
                    <div key={t.session_id} data-testid="billing-transaction" className="flex items-center justify-between gap-3 p-4">
                      <div className="min-w-0">
                        <div className="font-medium flex items-center gap-2"><Crown size={15} className="text-[hsl(var(--accent))]" /> {t.product}</div>
                        <div className="text-sm text-[hsl(var(--muted-foreground))] truncate">{t.event_title || "Account"} · {fmtDate(t.created_at)}</div>
                      </div>
                      <div className="font-semibold tabular-nums">{money(t.amount, t.currency)}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
